import { useEffect, useRef, useState } from "react";
import { Alert, Button, Field, Inline, Stack } from "@leylion/design-system";
import { createIdempotencyKey, request } from "../api/client";
import type { Customer } from "../api/cashier-types";
import { valueError } from "../shared/value-copy";
import { CashierCustomerSearchPicker } from "./CashierCustomerSearchPicker";

const emptyCustomers: Customer[] = [];
export function PreOrderCustomerPicker({ language, customer, onSelect, canCreate, disabled = false, onBusyChange }: {
  language: "ar" | "en"; customer: Customer | null; onSelect: (customer: Customer | null) => void;
  canCreate: boolean; disabled?: boolean; onBusyChange: (busy: boolean) => void;
}) {
  const ar = language === "ar";
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState(""); const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false); const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef<{ key: string; name: string; phone: string | null } | null>(null);
  const running = useRef(false); const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; onBusyChange(false); }; }, [onBusyChange]);
  const create = async () => {
    if (running.current || disabled || !canCreate || !name.trim()) return;
    pending.current ??= { key: createIdempotencyKey(), name: name.trim(), phone: phone.trim() || null };
    running.current = true; setBusy(true); onBusyChange(true); setError("");
    try {
      const saved = await request<Customer>("/api/v1/cashier/customers", { method: "POST",
        headers: { "Idempotency-Key": pending.current.key }, body: JSON.stringify({ name: pending.current.name,
          phone: pending.current.phone, email: null, notes: null, isActive: true, addresses: [], expectedVersion: null }) });
      if (!alive.current) return;
      onSelect(saved); setCreating(false); setUncertain(false); setName(""); setPhone(""); pending.current = null;
    } catch (caught) {
      if (!alive.current) return;
      const status = (caught as { status?: number })?.status;
      if (status && status >= 400 && status < 500) { pending.current = null; setUncertain(false); }
      else setUncertain(true);
      setError(valueError(caught, language));
    } finally { running.current = false; if (alive.current) { setBusy(false); onBusyChange(false); } }
  };
  return <Stack>
    <CashierCustomerSearchPicker language={language} label={ar ? "عميل CRM المرتبط بالحجز" : "Booking CRM customer"}
      value={customer?.id ?? ""} selectedCustomer={customer} initialCustomers={emptyCustomers} onSelect={onSelect}
      emptyLabel={ar ? "بدون عميل CRM" : "No CRM customer"} enabled={!disabled && !busy && !creating}
      searchTestId="preorder-customer-search" selectTestId="preorder-customer-select" />
    {canCreate && !creating ? <Button type="button" variant="secondary" disabled={disabled || busy}
      data-testid="preorder-new-customer" onClick={() => { setCreating(true); setError(""); }}>{ar ? "إنشاء عميل جديد" : "Create new customer"}</Button> : null}
    {creating ? <Stack>
      <Field data-testid="preorder-new-customer-name" label={ar ? "اسم العميل الجديد" : "New customer name"} value={name} disabled={busy || uncertain} onChange={e => setName(e.target.value)} />
      <Field data-testid="preorder-new-customer-phone" label={ar ? "هاتف العميل الجديد" : "New customer phone"} value={phone} disabled={busy || uncertain} onChange={e => setPhone(e.target.value)} />
      {error ? <Alert tone="danger" title={ar ? "تعذر إنشاء العميل" : "Customer creation failed"}>{error}</Alert> : null}
      {uncertain ? <p>{ar ? "النتيجة غير مؤكدة. أعد المحاولة للتحقق من نفس عملية الإنشاء، أو ابحث عن العميل قبل إنشاء سجل آخر." : "The result is uncertain. Retry to verify the same creation, or search for the customer before creating another record."}</p> : null}
      <Inline><Button type="button" variant="secondary" disabled={busy} onClick={() => { setCreating(false); pending.current = null; setUncertain(false); }}>{ar ? "إلغاء" : "Cancel"}</Button>
        <Button type="button" data-testid="preorder-save-new-customer" disabled={disabled || busy || !name.trim()} onClick={() => void create()}>{uncertain ? (ar ? "التحقق وإعادة المحاولة" : "Verify and retry") : (ar ? "إنشاء واختيار" : "Create and select")}</Button></Inline>
    </Stack> : null}
  </Stack>;
}
